/**
 * Voice level bus — the single place the UI asks "how loud is she right now?".
 *
 * Two sources, because she has two throats:
 *  1. Her cloud voice runs through an AudioContext, so we can tap the real
 *     output with an AnalyserNode and get honest frequency bands.
 *  2. A device voice (speechSynthesis) gives us no signal at all, so while it
 *     is talking we synthesise a breathing envelope instead of pretending to
 *     have data. It reads as speech rather than as a metronome.
 *
 * Everything here is best-effort and must never throw: this feeds decoration,
 * and decoration is not allowed to take the app down. The measuring loop only
 * runs while something is listening AND she is actually making sound, so an
 * idle tab costs nothing.
 */

export const VOICE_BANDS = 16;

type Listener = (bands: number[], rms: number) => void;

/** What the browser is doing with the microphone, in words a human can act on. */
export type MicState = "closed" | "opening" | "open" | "denied" | "unsupported" | "error";

export interface MicStatus {
  state: MicState;
  /** Plain-language description of what happened. */
  message: string;
  /** What to do about it, when there is something to do. */
  hint: string;
  /** Did the browser actually give us noise suppression + echo cancellation? */
  clean: boolean;
}

export interface MicLatency {
  /** Onset of your voice → the level bus publishing it (border/jaw movement). */
  pulseMs: number;
  /** Onset of your voice → her face state changing in response. */
  reactMs: number;
  /** Rolling average of pulseMs, so the readout is not jittery. */
  avgMs: number;
}

type Bus = {
  listeners: Set<Listener>;
  analyser: AnalyserNode | null;
  freq: Uint8Array<ArrayBuffer> | null;
  // Live microphone tap: your voice going *into* her, read the same way as
  // hers coming out, so the border and her face move while you talk too.
  mic: AnalyserNode | null;
  micFreq: Uint8Array<ArrayBuffer> | null;
  micCtx: AudioContext | null;
  micStream: MediaStream | null;
  raf: number | null;
  bands: number[];
  rms: number;
  status: MicStatus;
  statusListeners: Set<(s: MicStatus) => void>;
  latency: MicLatency;
  onset: number;
  awaitingReact: boolean;
};


// Pinned to one shared object per page. Chunk splitting, a hot reload or a
// second copy of this module would otherwise give the voice engine and the
// border their own private state, and the border would never move.
const KEY = "__sophiaVoiceBus";

function bus(): Bus {
  const host = (typeof globalThis !== "undefined" ? globalThis : {}) as Record<string, unknown>;
  let b = host[KEY] as Bus | undefined;
  if (!b) {
    b = {
      listeners: new Set<Listener>(),
      analyser: null,
      freq: null,
      mic: null,
      micFreq: null,
      micCtx: null,
      micStream: null,
      raf: null,
      // Latest reading, so a component mounting mid-sentence starts warm.
      bands: new Array(VOICE_BANDS).fill(0),
      rms: 0,
      status: { state: "closed", message: "Microphone closed.", hint: "", clean: false },
      statusListeners: new Set<(s: MicStatus) => void>(),
      latency: { pulseMs: 0, reactMs: 0, avgMs: 0 },
      onset: 0,
      awaitingReact: false,
    };

    host[KEY] = b;
  }
  return b;
}

/** Tap her cloud voice bus. Called by cloudTts each time a reply starts. */
export function tapVoiceBus(ctx: AudioContext, node: AudioNode): void {
  const b = bus();
  try {
    const a = ctx.createAnalyser();
    a.fftSize = 256;
    a.smoothingTimeConstant = 0.72;
    // A tap, not an insert: the node keeps its own path to the speakers, so a
    // failure in here can never mute her.
    node.connect(a);
    b.analyser = a;
    b.freq = new Uint8Array(new ArrayBuffer(a.frequencyBinCount));
    kick();
  } catch {
    b.analyser = null;
    b.freq = null;
  }
}

/** Device speech is the fallback source; check it without importing tts. */
function deviceSpeaking(): boolean {
  try {
    const s = typeof window !== "undefined" ? window.speechSynthesis : null;
    return !!s && (s.speaking || s.pending);
  } catch {
    return false;
  }
}

/** Whichever tap currently has sound in it: her own voice wins over the mic. */
function source(): { a: AnalyserNode; f: Uint8Array<ArrayBuffer>; gain: number } | null {
  const b = bus();
  const hers = b.analyser && b.freq ? { a: b.analyser, f: b.freq, gain: 1.35 } : null;
  // Your voice comes in quieter than her rendered output, so it gets more gain.
  const yours = b.mic && b.micFreq ? { a: b.mic, f: b.micFreq, gain: 2.1 } : null;
  for (const s of [hers, yours]) {
    if (!s) continue;
    try {
      s.a.getByteFrequencyData(s.f);
    } catch {
      continue;
    }
    for (let i = 0; i < s.f.length; i += 1) if (s.f[i]! > 3) return s;
  }
  return null;
}

let current: { a: AnalyserNode; f: Uint8Array<ArrayBuffer>; gain: number } | null = null;

function analyserLive(): boolean {
  current = source();
  return current !== null;
}

function readAnalyser(): void {
  const b = bus();
  const s = current;
  if (!s) return;
  const f = s.f;
  // Log-ish grouping: speech energy crowds the low bins, so an even split would
  // leave the upper two-thirds of the border dead.
  const out = new Array<number>(VOICE_BANDS).fill(0);
  const usable = Math.floor(f.length * 0.72);
  let sum = 0;
  for (let i = 0; i < VOICE_BANDS; i += 1) {
    const lo = Math.floor(Math.pow(i / VOICE_BANDS, 1.6) * usable);
    const hi = Math.max(lo + 1, Math.floor(Math.pow((i + 1) / VOICE_BANDS, 1.6) * usable));
    let peak = 0;
    for (let k = lo; k < hi && k < f.length; k += 1) peak = Math.max(peak, f[k]!);
    const v = Math.min(1, (peak / 255) * s.gain);
    out[i] = Number.isFinite(v) ? v : 0;
    sum += out[i]!;
  }
  b.bands = out;
  b.rms = Math.min(1, sum / VOICE_BANDS);
}

// ---- Microphone status: say out loud what the browser is doing -------------

function setStatus(state: MicState, message: string, hint = "", clean?: boolean): void {
  const b = bus();
  b.status = { state, message, hint, clean: clean ?? b.status.clean };
  const snap = b.status;
  for (const fn of b.statusListeners) {
    try { fn(snap); } catch { /* a broken listener must not break the mic */ }
  }
}

/** Current microphone permission / error state. */
export function micStatus(): MicStatus {
  return bus().status;
}

/** Watch the microphone status so the UI can explain a block as it happens. */
export function subscribeMicStatus(fn: (s: MicStatus) => void): () => void {
  const b = bus();
  b.statusListeners.add(fn);
  try { fn(b.status); } catch { /* ignore */ }
  return () => { b.statusListeners.delete(fn); };
}

/** How fast your speech shows up in her pulse and her face, in milliseconds. */
export function micLatency(): MicLatency {
  return bus().latency;
}

/**
 * Called by the face side of the app when her state flips in response to you,
 * closing the loop on the react-latency measurement.
 */
export function markFaceReaction(): void {
  const b = bus();
  if (!b.awaitingReact || !b.onset) return;
  b.awaitingReact = false;
  const ms = Math.max(0, Math.round(performance.now() - b.onset));
  if (ms < 4000) b.latency = { ...b.latency, reactMs: ms };
}

/** Audio constraints that keep a room out of her ear. */
function micConstraints(): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
  };
}

/** Did the track really come back with the cleaning turned on? */
function trackIsClean(stream: MediaStream): boolean {
  try {
    const s = stream.getAudioTracks()[0]?.getSettings() as
      | { echoCancellation?: boolean; noiseSuppression?: boolean }
      | undefined;
    // Some browsers omit the fields entirely while still honouring them, so an
    // absent flag is treated as "probably on" rather than a failure.
    return s?.echoCancellation !== false && s?.noiseSuppression !== false;
  } catch {
    return false;
  }
}

/**
 * Open the live microphone tap — your voice going into her. Safe to call more
 * than once; the stream is shared and reference-free. Failures no longer go
 * quiet: they publish a status the UI can show you.
 */
export async function startMicLevel(): Promise<boolean> {
  const b = bus();
  if (b.mic) return true;
  setStatus("opening", "Asking the browser for your microphone…");
  try {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus(
        "unsupported",
        "This browser gives no microphone access at all.",
        "Open her in Chrome, Edge or Safari over https — file:// and old in-app browsers cannot record.",
        false,
      );
      return false;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(micConstraints());
    } catch (first) {
      // A device that rejects the cleaning constraints still deserves a mic, so
      // fall back to a bare request rather than failing the whole call.
      const name = (first as { name?: string } | null)?.name ?? "";
      if (name === "OverconstrainedError" || name === "NotReadableError") {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } else throw first;
    }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) {
      stream.getTracks().forEach((t) => t.stop());
      setStatus("unsupported", "This browser has no audio engine.", "Try Chrome or Safari.", false);
      return false;
    }
    const ctx = new Ctx();
    try { await ctx.resume(); } catch { /* some browsers resume lazily */ }
    const a = ctx.createAnalyser();
    a.fftSize = 256;
    a.smoothingTimeConstant = 0.7;
    ctx.createMediaStreamSource(stream).connect(a);
    b.micCtx = ctx;
    b.micStream = stream;
    b.mic = a;
    b.micFreq = new Uint8Array(new ArrayBuffer(a.frequencyBinCount));
    quiet = 0;
    const clean = trackIsClean(stream);
    setStatus(
      "open",
      clean
        ? "Microphone live — noise suppression and echo cancellation on."
        : "Microphone live, but this device would not turn on noise cleaning.",
      clean ? "" : "Expect room echo; a headset mic will sound much cleaner to her.",
      clean,
    );
    kick();
    return true;
  } catch (err) {
    const name = (err as { name?: string } | null)?.name ?? "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      setStatus(
        "denied",
        "The browser blocked your microphone.",
        "Tap the padlock in the address bar → Site settings → Microphone → Allow, then press talk again.",
        false,
      );
    } else if (name === "NotFoundError" || name === "OverconstrainedError") {
      setStatus(
        "error",
        "No microphone was found on this device.",
        "Plug one in or pick the right input in your system sound settings, then press talk again.",
        false,
      );
    } else if (name === "NotReadableError" || name === "AbortError") {
      setStatus(
        "error",
        "Another app is holding the microphone.",
        "Close other calls or recorders, then press talk again.",
        false,
      );
    } else {
      setStatus("error", "The microphone could not be opened.", "Reload her and try once more.", false);
    }
    return false;
  }
}

/** Close the live microphone tap and release the device. */
export function stopMicLevel(): void {
  const b = bus();
  b.mic = null;
  b.micFreq = null;
  current = null;
  b.awaitingReact = false;
  b.onset = 0;
  try { b.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
  b.micStream = null;
  try { void b.micCtx?.close(); } catch { /* ignore */ }
  b.micCtx = null;
  if (b.status.state === "open" || b.status.state === "opening") {
    setStatus("closed", "Microphone closed.", "");
  }
}

/** Is she hearing you live right now? */
export function micLevelOpen(): boolean {
  return bus().mic !== null;
}


function readSynthetic(t: number): void {
  // Detuned waves per band read as breath and syllables rather than a sine
  // sweep. Purely cosmetic: it only runs when we truly have no signal.
  const b = bus();
  const out = new Array<number>(VOICE_BANDS).fill(0);
  const speech = 0.55 + 0.45 * Math.sin(t / 190);
  let sum = 0;
  for (let i = 0; i < VOICE_BANDS; i += 1) {
    const p = i / VOICE_BANDS;
    const v = Math.max(
      0,
      Math.min(
        1,
        (0.22 +
          0.5 * Math.abs(Math.sin(t / 130 + i * 0.7)) +
          0.28 * Math.abs(Math.sin(t / 61 + i * 1.9))) *
          speech *
          (1 - p * 0.45),
      ),
    );
    out[i] = v;
    sum += v;
  }
  b.bands = out;
  b.rms = Math.min(1, sum / VOICE_BANDS);
}

function silence(): void {
  // Fall away rather than snapping to zero, so the border settles.
  const b = bus();
  let any = false;
  b.bands = b.bands.map((v) => {
    const n = v * 0.82;
    if (n > 0.004) any = true;
    return n > 0.004 ? n : 0;
  });
  b.rms = any ? b.rms * 0.82 : 0;
}

// Frames of pure silence before the loop parks itself. Running at animation
// rate all day for a border that is invisible would cost battery for nothing,
// so once she is quiet we drop to a cheap poll and wake on the next sound.
const PARK_AFTER = 40;
let quiet = 0;

// Latency measurement. An onset is the first frame where your voice rises out
// of silence; from there we can honestly account for the whole path: the
// device's own input buffer, the analyser window, and the time it takes this
// loop to hand the reading to the border and her jaw.
const ONSET_ON = 0.09;
const ONSET_OFF = 0.04;
let wasLoud = false;

function inputLagMs(): number {
  const b = bus();
  const ctx = b.micCtx;
  const a = b.mic;
  if (!ctx || !a) return 0;
  const base = (typeof ctx.baseLatency === "number" ? ctx.baseLatency : 0.01) * 1000;
  const window = (a.fftSize / (ctx.sampleRate || 48000)) * 1000;
  return base + window;
}

function measureLatency(frameStart: number, micDriven: boolean, level: number): void {
  const b = bus();
  if (!micDriven) { wasLoud = false; return; }
  if (!wasLoud && level >= ONSET_ON) {
    wasLoud = true;
    b.onset = frameStart;
    b.awaitingReact = true;
    const pulseMs = Math.max(0, Math.round(inputLagMs() + (performance.now() - frameStart)));
    const avg = b.latency.avgMs > 0 ? b.latency.avgMs * 0.7 + pulseMs * 0.3 : pulseMs;
    b.latency = { ...b.latency, pulseMs, avgMs: Math.round(avg) };
  } else if (wasLoud && level < ONSET_OFF) {
    wasLoud = false;
  }
}

function tick(t: number): void {
  const b = bus();
  b.raf = null;
  if (b.listeners.size === 0) return;
  const live = analyserLive();
  const speaking = live || deviceSpeaking();
  if (live) readAnalyser();
  else if (speaking) readSynthetic(t);
  else silence();
  quiet = speaking || b.rms > 0 ? 0 : quiet + 1;

  const snapshot = b.bands;
  const level = b.rms;
  // Only your own tap counts for latency — her rendered voice is not a probe.
  measureLatency(t, live && current !== null && current.a === b.mic, level);
  for (const fn of b.listeners) {
    try {
      fn(snapshot, level);
    } catch {
      /* a bad listener must not stall the bus */
    }
  }
  if (quiet >= PARK_AFTER) park();
  else kick();
}


/** Idle watch: no frames, just a light heartbeat until sound returns. */
function park(): void {
  const b = bus();
  if (b.raf !== null) return;
  try {
    window.setTimeout(() => {
      if (bus().listeners.size === 0) return;
      quiet = 0;
      kick();
    }, 400);
  } catch {
    kick();
  }
}

function kick(): void {
  const b = bus();
  if (b.raf !== null || b.listeners.size === 0) return;
  if (typeof requestAnimationFrame !== "function") return;
  try {
    b.raf = requestAnimationFrame(tick);
  } catch {
    b.raf = null;
  }
}

/** Subscribe to her voice shape. Returns an unsubscribe. */
export function subscribeVoiceLevel(fn: Listener): () => void {
  const b = bus();
  b.listeners.add(fn);
  kick();
  return () => {
    b.listeners.delete(fn);
    if (b.listeners.size === 0 && b.raf !== null) {
      try {
        cancelAnimationFrame(b.raf);
      } catch {
        /* ignore */
      }
      b.raf = null;
    }
  };
}

/** Last published reading, for a component that wants a warm first paint. */
export function lastVoiceReading(): { bands: number[]; rms: number } {
  const b = bus();
  return { bands: b.bands, rms: b.rms };
}
